import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import PropTypes from 'prop-types';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import storeIcon from '../../assets/SVGs/Vector.svg';
import Location from './Location';
import {
	NEED_TO_BE_SELECTED,
	measures,
	displayMetric,
	metricDomain,
	productHierarchyType,
	BOGLE_LIGHT,
	measureList,
	measureListB,
	aggregationList,
	aggregationListB,
	hierarchyArr,
} from '../../constants/constant';
import GroupedMetric from './GroupedMetric';
import AddItem from './AddItems/AddItem';
import { getKeyByValue } from '../../helper/commonUtils';
import { Tooltip, HotSpot } from './FT-UX';
import { useStyles, styles, selectStyleMenu } from './styles';
import { getStoredTooltipData } from './FT-UX/util';

const getEditData = (arr, key) => {
	return arr.find((obj) => {
		return obj.name === key;
	});
};

const Threshold = ({ width, getData, editRuleData, itemName }) => {
	const classes = useStyles();
	const measureEditValue = getEditData(
		measureList,
		editRuleData?.left?.measureType
	);
	const aggregateEditValue = getEditData(
		aggregationList,
		editRuleData?.left?.aggregationLevel
	);
	const [metric, setMetric] = useState(NEED_TO_BE_SELECTED);
	const [hierarchy, setHierarchy] = useState(
		editRuleData
			? getKeyByValue(
					productHierarchyType,
					editRuleData?.left?.productHierarchyType
			  )
			: 'Category'
	);
	const [condition, setCondition] = useState(
		editRuleData
			? editRuleData?.operation === 'GreaterThan'
				? 'Above'
				: 'Below'
			: 'Above'
	);
	const [measure, setMeasure] = useState(
		editRuleData ? measureEditValue?.name : 'ABSOLUTE_VALUE'
	);
	const [aggregate, setAggregate] = useState(
		editRuleData ? aggregateEditValue?.name : 'DAILY'
	);
	const [amount, setAmount] = useState(
		editRuleData ? editRuleData?.threshold : undefined
	);
	const [error, setError] = useState(false);
	const [locationData, setLocationData] = useState({});
	const [expandMetric, setExpandMetric] = useState(false);
	const [expandTR, setExpandTR] = useState(false);
	const [icon, setIcon] = useState(false);
	const [hierarchyList, setHeirarchyList] = useState({});
	const storedValue = getStoredTooltipData();

	const getExecutionType = () => {
		if (hierarchyList?.alertForIndividual) {
			return 'INDIVIDUAL';
		} else {
			return 'AGGREGATE';
		}
	};

	useEffect(() => {
		const {
			location,
			channels,
			locationFilters,
			channelType,
			executionTypeForLocation,
		} = locationData;
		const data = {
			metricDomain: metricDomain[metric],
			metric: measures[metric],
			channelType,
			location: location === null ? 'USA' : location,
			channels,
			locationFilters,
			executionTypeForLocation,
			productHierarchyType: productHierarchyType[hierarchy],
			executionTypeForProduct: getExecutionType(),
			productFilters: hierarchyList?.selectedItems,
			measureType: measure,
			aggregationLevel: aggregate,
			status: 'Active',
			threshold: amount,
			operation: condition === 'Above' ? 'GreaterThan' : 'LessThanEquals',
			conditionType: 'Threshold',
			itemName,
			error,
		};
		getData(data);
	}, [
		metric,
		hierarchy,
		hierarchyList,
		condition,
		measure,
		aggregate,
		amount,
		locationData,
		itemName,
	]);

	useEffect(() => {
		checkEditThresholdValue(amount);
	}, [measure]);

	const getLocationData = (data) => {
		setLocationData(data);
	};
	const getHeirarchySelection = (list) => {
		setHeirarchyList(list);
	};
	const handleMetricChange = (label) => {
		setMetric(label);
		setExpandMetric(false);
	};

	const handleHierarchyChange = (event) => {
		if (hierarchy) {
			getHeirarchySelection({});
		}
		setHierarchy(event.target.value);
	};
	const handleConditionChange = (event) => {
		setCondition(event.target.value);
	};
	const checkEditThresholdValue = (value) => {
		if (validateAmount()?.test(value) || !value) {
			setError(false);
		} else {
			setError(true);
		}
	};

	const handleMeasureChange = (event) => {
		setMeasure(event.target.value);
		setAggregate();
	};
	const handleAggregateChange = (event) => {
		setAggregate(event.target.value);
		if (measure) {
			setExpandTR(false);
		}
	};

	const validateAmount = (errorMessage = false) => {
		// regrex for max 12 digit upto 2 decimal points
		const regexpDollar = /^([\d]{0,12})(\.[\d]{0,2})?$/;
		// regrex for max 3 digit upto 2 decimal points
		const regexpPercentage = /^([\d]{0,3})(\.[\d]{0,2})?$/;
		if (
			[
				'Store In InStock %',
				'Nil Pick Rate %',
				'Pre substitute Rate %',
				'Post substitute Rate %',
				'First-time Pick Rate %',
			].includes(displayMetric[metric]) ||
			([
				'On Hand (Units)',
				'On Order (Units)',
				'DC To Store (Units)',
				'Store In Warehouse (Units)',
			].includes(displayMetric[metric]) &&
				['YOY_CHANGE_PERCENTAGE', 'WOW_CHANGE_PERCENTAGE'].includes(measure)) ||
			([
				'Net Sales',
				'GMV',
				'On Hand ($)',
				'On Order ($)',
				'DC To Store ($)',
				'Store In Warehouse ($)',
			].includes(displayMetric[metric]) &&
				['YOY_CHANGE_PERCENTAGE', 'WOW_CHANGE_PERCENTAGE'].includes(measure))
		) {
			return errorMessage ? 3 : regexpPercentage;
		}
		return errorMessage ? 12 : regexpDollar;
	};

	const handleChange = (e) => {
		const { value } = e.target;
		if (value.match(validateAmount()) || value === '') {
			setError(false);
		} else {
			setError(true);
		}
		setAmount(value);
	};
	const handleIconClick = () => {
		setIcon(true);
	};

	const style = {
		'& label.Mui-focused': {
			color: '#3C228A',
		},
		'& .MuiOutlinedInput-root': {
			'&.Mui-focused fieldset': {
				borderColor: '#3C228A',
			},
		},
		width: width,
		marginBottom: '20px',
	};

	const measureArr = [
		'Store In InStock %',
		'Nil Pick Rate %',
		'Pre substitute Rate %',
		'Post substitute Rate %',
		'First-time Pick Rate %',
	].includes(displayMetric[metric])
		? measureListB
		: measureList;

	const aggregationArr = ['WOW_CHANGE', 'WOW_CHANGE_PERCENTAGE'].includes(
		measure
	)
		? aggregationListB
		: aggregationList;

	const borderTopStyle = '1px solid lightgray';

	return (
		<Box sx={{ marginTop: '25px' }}>
			{/* Wrapped w/ Tooltip for FT UX */}
			<Tooltip
				placement={'bottom-end'}
				type={'metric'}
				show={storedValue?.showMetricTooltip}>
				<Accordion
					expanded={expandMetric === true}
					onChange={() => setExpandMetric((prevCheck) => !prevCheck)}
					elevation={0}
					classes={{
						root: classes.MuiAccordionroot,
					}}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="Metric-content"
						id="Metric-header"
						sx={{ borderTop: borderTopStyle }}>
						<Box className={classes.boxStyle}>
							<Typography className={classes.menuItems}>Metric</Typography>
							{/* Hot spot for FT UX  */}
							<HotSpot
								show={storedValue?.showMetricTooltip}
								left={'118%'}
								top={'70%'}>
								<Typography
									className={classes.menuItems}
									style={{ fontSize: 12, paddingTop: '6px' }}>
									{metric !== NEED_TO_BE_SELECTED
										? displayMetric[metric]
										: metric}
								</Typography>
							</HotSpot>
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
							<GroupedMetric
								getValue={handleMetricChange}
								defaultValue={editRuleData?.left?.metric || 'NET_SALES'}
								isEditFlow={editRuleData}
							/>
						</FormControl>
					</AccordionDetails>
				</Accordion>
			</Tooltip>

			<Location
				metric={displayMetric[metric]}
				metricDomain={metricDomain[metric]}
				getLocationData={getLocationData}
				editRuleData={editRuleData?.left}
			/>

			<Accordion
				elevation={0}
				classes={{
					root: classes.MuiAccordionroot,
				}}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="hierarchy-content"
					id="hierarchy-header"
					sx={{ borderTop: borderTopStyle }}>
					<Box className={classes.boxStyle}>
						<Typography className={classes.menuItems}>
							Product Hierarchy
						</Typography>
						<Typography
							className={classes.menuItems}
							style={{ fontSize: 12, paddingTop: '6px' }}>
							{`${hierarchy} ${
								hierarchyList?.selectedItems?.length
									? `, multiple ${hierarchy} selected`
									: ''
							} `}
						</Typography>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
						<InputLabel id="hierarchy-label" sx={{ ...styles.textField }}>
							Hierarchy
						</InputLabel>
						<Select
							id="Hierarchy"
							value={hierarchy}
							label="Hierarchy"
							onChange={handleHierarchyChange}
							sx={{
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{hierarchyArr?.map((name) => (
								<MenuItem key={name} value={name} className={classes.menuItems}>
									{name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{hierarchy !== NEED_TO_BE_SELECTED && (
						<Tooltip
							show={storedValue?.showAddItemTooltip}
							placement="bottom-start"
							type={'addItem'}>
							{/* Wrapped w/ Tooltip for FT UX */}
							<Box
								style={{
									marginTop: '15px',
									display: 'flex',
									alignItems: 'center',
									cursor: 'pointer',
								}}
								onClick={handleIconClick}>
								{/* Hot spot for FT UX  */}
								<HotSpot
									show={storedValue?.showAddItemTooltip}
									top={'50%'}
									left={'138%'}>
									<img alt="storeIcon" src={storeIcon} />
									<span style={{ marginLeft: '10px' }}> Add {hierarchy}</span>
								</HotSpot>
							</Box>
						</Tooltip>
					)}
				</AccordionDetails>
			</Accordion>

			<Accordion
				expanded={expandTR === true}
				onChange={() => setExpandTR((prevCheck) => !prevCheck)}
				elevation={0}
				classes={{
					root: classes.MuiAccordionroot,
				}}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="timerange-content"
					id="timerange-header"
					sx={{ borderTop: borderTopStyle }}>
					<Box className={classes.boxStyle}>
						<Typography className={classes.menuItems}>Time Range</Typography>
						<Typography
							className={classes.menuItems}
							style={{ fontSize: '12px', paddingTop: '6px' }}>
							{measure && aggregate
								? `${measure}, ${aggregate}`
								: NEED_TO_BE_SELECTED}
						</Typography>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField }}>
							Measure
						</InputLabel>
						<Select
							id="measure-basic"
							value={measure}
							label="Measure"
							onChange={handleMeasureChange}
							sx={{
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{measureArr?.map((data) => (
								<MenuItem
									key={data.name}
									value={data.name}
									className={classes.menuItems}>
									{data.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl
						sx={{ ...styles.metricSelect, ...styles.outline }}
						style={{ marginTop: '30px' }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField }}>
							Aggregate
						</InputLabel>

						<Select
							id="aggregate-basic"
							value={aggregate}
							label="Aggregate"
							onChange={handleAggregateChange}
							sx={{
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{aggregationArr?.map((data) => (
								<MenuItem
									key={data.name}
									value={data.name}
									className={classes.menuItems}>
									{data.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</AccordionDetails>
			</Accordion>
			<Accordion
				elevation={0}
				classes={{
					root: classes.MuiAccordionroot,
				}}
				defaultExpanded={true}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="delta-content"
					id="delta-header"
					sx={{ borderTop: '1px solid lightgray' }}>
					<Box className={classes.boxStyle}>
						<Typography className={classes.menuItems}>Threshold</Typography>
						<Typography
							className={classes.menuItems}
							style={{ fontSize: 12, paddingTop: '6px' }}>
							{!amount
								? NEED_TO_BE_SELECTED
								: `${condition},
							${amount}`}
						</Typography>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
						<InputLabel id="Threshold-label" sx={{ ...styles.textField }}>
							Condition
						</InputLabel>
						<Select
							id="Condition"
							value={condition}
							label="Condition"
							onChange={handleConditionChange}
							sx={{
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							<MenuItem className={classes.menuItems} value={'Above'}>
								Above
							</MenuItem>
							<MenuItem className={classes.menuItems} value={'Below'}>
								Below
							</MenuItem>
						</Select>
					</FormControl>
					<TextField
						error={error}
						id="deltaamount-basic"
						label="Threshold Value"
						name="thresholdValue"
						variant="outlined"
						inputProps={{ style: { ...styles.textField } }}
						InputLabelProps={{ style: { ...styles.textField } }}
						sx={style}
						style={{ marginTop: '20px', width: '100%' }}
						value={amount}
						onChange={handleChange}
						helperText={
							error
								? `Please enter the maximum of ${validateAmount(
										true
								  )} number only upto 2 decimal places`
								: ''
						}
					/>
				</AccordionDetails>
			</Accordion>
			<AddItem
				isOpen={icon ? true : false}
				type={hierarchy === NEED_TO_BE_SELECTED ? 'Item' : hierarchy}
				setIsAddItemsOpen={setIcon}
				getData={getHeirarchySelection}
			/>
		</Box>
	);
};

Threshold.propTypes = {
	width: PropTypes.number,
	getData: PropTypes.func,
	editRuleData: PropTypes.object,
	itemName: PropTypes.string,
};

export default Threshold;
